import { MODAL_UPGRADE_PLAN } from '@components/App/Modal/constants'
import { usePasteFromToolbar } from '@components/ArtBoard/ArtboardToolbar'
import { useSelection } from '@components/ArtBoard/Node/hooks'
import useActions from '@components/ArtBoard/Node/hooks/useActions'
import type { ID, NodeVariant } from '@octopus-do/octopus-project-core'
import { BlockActions } from '@store/blockSaga'
import {
  thunkChangeSelectedNodesColor,
  thunkCopySelectedNodes,
  thunkUpdateNodeUrl,
} from '@store/migrationActions/thunkActions/thunkNodesActions'
import { artBoardActions } from '@store/reducers/artboard'
import { SeoActions } from '@store/reducers/seo/actions'
import type { AppState } from '@store/types'
import { setSelectedNodeLink } from '@storeOld/reducers/canvas/canvas'
import {
  NotificationType,
  notificationActionCreators,
} from '@storeOld/reducers/notification'
import { openModal } from '@storeOld/reducers/ui'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { useProjectAccess } from '@/hooks'
import { useGenerateBlocks } from '@/hooks/ai'
import { useAiGenerating } from '@/hooks/selectors/useAiGenerating'
import { PAGE_PERMISSION } from '@/permissions/project/definitions'

export const useNodeToolbar = (nodeId: ID) => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()

  const { onChangeNodeFrame, onDeleteNode, onDuplicateNode } =
    useActions(nodeId)
  const selection = useSelection(nodeId)
  const generateBlocks = useGenerateBlocks(nodeId)
  const { onPaste, pasting } = usePasteFromToolbar('node')
  const aiWorking = useAiGenerating()

  const multiple = useSelector<AppState, boolean>(
    ({ canvas }) => canvas.node.selectedNodes.length > 1
  )

  const arrowsAccess = useProjectAccess(PAGE_PERMISSION.ARROWS)
  const frameAccess = useProjectAccess(PAGE_PERMISSION.FRAME)
  const tagAccess = useProjectAccess(PAGE_PERMISSION.TAG)
  const linkAccess = useProjectAccess(PAGE_PERMISSION.LINK)
  const duplicateAccess = useProjectAccess(PAGE_PERMISSION.DUPLICATE)

  const notifyCopied = () => {
    dispatch(notificationActionCreators.setClearAllNotification())
    dispatch(
      notificationActionCreators.setNotification({
        type: NotificationType.COPY_SUCCESSFUL,
      })
    )
  }

  const onAddBlock = () => {
    sendGtmEvent(GtmTriggerName.BLOCK_ADD)
    dispatch(BlockActions.addBlock())
  }

  const onChangeFrame = (variant: NodeVariant) => {
    sendGtmEvent(GtmTriggerName.PAGE_FRAME, 'Click to change node frame type')
    onChangeNodeFrame(variant)
  }

  const onUpgradeFrame = () => {
    sendGtmEvent(GtmTriggerName.UPGRADE_PAGE_FRAME)
    dispatch(openModal(MODAL_UPGRADE_PLAN))
  }

  const onChangeColor = (colorId: ID) => {
    dispatch(thunkChangeSelectedNodesColor(colorId))
  }

  const onUpgradeTags = () => {
    sendGtmEvent(GtmTriggerName.UPGRADE_TAGS)
    dispatch(
      openModal({
        modalType: MODAL_UPGRADE_PLAN,
        modalProps: { slideType: 'tags' },
      })
    )
  }

  const onArrows = () => {
    sendGtmEvent(
      GtmTriggerName.ARROW_ADD,
      'Click on arrow button in page toolbar'
    )
    dispatch(artBoardActions.startArrows({ id: nodeId, kind: 'node' }))
  }

  const onSeo = () => {
    selection.clearSelection()
    dispatch(SeoActions.enableMode())
    dispatch(SeoActions.show(nodeId))
  }

  const onLink = (url: string) => {
    sendGtmEvent(GtmTriggerName.PAGE_LINK, 'Add/Change url to node')
    dispatch(thunkUpdateNodeUrl(nodeId, url))
  }

  const onRemoveLink = () => {
    dispatch(thunkUpdateNodeUrl(nodeId, ''))
  }

  const onGenerate = (prompt: string) => {
    sendGtmEvent(
      GtmTriggerName.AI_BLOCK_GENERATE,
      'Click to generate blocks inside of page'
    )

    generateBlocks(prompt)
  }

  const onCopy = () => {
    notifyCopied()
    dispatch(thunkCopySelectedNodes())
  }

  const onCopyTree = () => {
    notifyCopied()
    dispatch(thunkCopySelectedNodes({ copyTree: true }))
  }

  return {
    actions: {
      onAddBlock,
      onArrows,
      onChangeColor,
      onChangeFrame,
      onCopy,
      onCopyTree,
      onDelete: onDeleteNode,
      onDuplicate: onDuplicateNode,
      onGenerate,
      onLink,
      onPaste,
      onRemoveLink,
      onSeo,
      onUpgradeFrame,
      onUpgradeTags,
    },
    addBlockDisabled: multiple,
    arrowsDisabled: multiple || arrowsAccess.hasNotAvailableAccess,
    copyTreeDisabled: multiple,
    duplicateDisabled: multiple || duplicateAccess.hasNotAvailableAccess,
    frameLocked: frameAccess.hasNotAvailableAccess,
    generateDisabled: multiple,
    generating: aiWorking,
    linkDisabled: multiple || linkAccess.hasNotAvailableAccess,
    pasteDisabled: multiple,
    pasting,
    seoDisabled: multiple,
    tagsLocked: tagAccess.hasNotAvailableAccess,
  }
}

// While a toolbar popover with a text field is open the keyboard shortcuts
// for the selected page (copy, paste, delete, enter) must stay out of it.
export const useInputGuard = (nodeId: ID, active: boolean) => {
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(setSelectedNodeLink(active ? nodeId : null))

    return () => {
      dispatch(setSelectedNodeLink(null))
    }
  }, [active])
}
